import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton } from "@mui/material";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { Carousel } from "components/Carousel";
import { usePaginatedQuery } from "hooks/usePaginatedQuery";
import { Banner } from "types/models";
import {
  getFloatingBannersState,
  setFloatingBannersState,
} from "utils/cookies";
import { isDateNewerThanADayAgo } from "utils/date";

type FloatingBannersContextValue = {
  showFloatingBannersButton: boolean;
  onOpenFloatingBanners: () => void;
  FloatingBannersModal: () => JSX.Element | null;
};

const FloatingBannersContext =
  createContext<FloatingBannersContextValue | null>(null);

export const FloatingBannersProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [isManuallyOpen, setIsManuallyOpen] = useState(false);
  const [dismissedInSessionKey, setDismissedInSessionKey] = useState("");

  const { data: floatingBanners, isLoading: isLoadingFloatingBanners } =
    usePaginatedQuery<Banner>({
      queryKey: ["floatingBanners"],
      path: "banners?active=true&per=100&banner_type=floating",
    });

  const floatingBannerItems = useMemo(
    () => floatingBanners?.results ?? [],
    [floatingBanners]
  );
  const floatingBannerIds = useMemo(
    () => floatingBannerItems.map(({ id }) => id).sort(),
    [floatingBannerItems]
  );
  const floatingBannerKey = useMemo(
    () => floatingBannerIds.join(","),
    [floatingBannerIds]
  );

  const shouldAutoShowFloatingBanner = useMemo(() => {
    if (isLoadingFloatingBanners || !floatingBannerIds.length) return false;

    if (dismissedInSessionKey === floatingBannerKey) return false;

    const state = getFloatingBannersState();
    const previousIds = state?.ids ? [...state.ids].sort().join(",") : "";
    const expired = !state || !isDateNewerThanADayAgo(state.dismissedAt);
    const idsChanged = previousIds !== floatingBannerKey;

    return expired || idsChanged;
  }, [
    dismissedInSessionKey,
    floatingBannerIds.length,
    floatingBannerKey,
    isLoadingFloatingBanners,
  ]);

  const isFloatingBannersModalOpen =
    isManuallyOpen || shouldAutoShowFloatingBanner;

  const handleCloseFloatingBanner = useCallback(() => {
    setFloatingBannersState({
      ids: floatingBannerIds,
      dismissedAt: Date.now(),
    });
    setDismissedInSessionKey(floatingBannerKey);
    setIsManuallyOpen(false);
  }, [floatingBannerIds, floatingBannerKey]);

  const FloatingBannersModal = useCallback(() => {
    if (
      isLoadingFloatingBanners ||
      !floatingBannerItems.length ||
      !isFloatingBannersModalOpen
    ) {
      return null;
    }

    return (
      <Box
        onClick={handleCloseFloatingBanner}
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: 1400,
          backgroundColor: "rgba(0, 0, 0, 0.65)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: { xs: 1.5, md: 3 },
        }}
      >
        <IconButton
          size="large"
          onClick={(event) => {
            event.stopPropagation();
            handleCloseFloatingBanner();
          }}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            zIndex: 1,
            backgroundColor: "rgba(255,255,255,0.85)",
          }}
        >
          <CloseIcon />
        </IconButton>
        <Box
          onClick={(event) => event.stopPropagation()}
          sx={{
            width: "100%",
            maxWidth: "980px",
            borderRadius: 1.5,
            overflow: "hidden",
          }}
        >
          <Carousel
            items={floatingBannerItems}
            indicators
            style={{ marginTop: 0, borderRadius: 1.5 }}
            imageStyles={{
              height: { xs: 260, sm: 360, md: 520 },
              objectFit: "contain",
              cursor: "pointer",
            }}
            onImageClick={(_, item) => {
              if (!item.link) return;
              window.open(item.link, "_blank");
            }}
          />
        </Box>
      </Box>
    );
  }, [
    floatingBannerItems,
    handleCloseFloatingBanner,
    isFloatingBannersModalOpen,
    isLoadingFloatingBanners,
  ]);

  const onOpenFloatingBanners = useCallback(() => setIsManuallyOpen(true), []);

  const value = useMemo(
    () => ({
      showFloatingBannersButton: floatingBannerItems.length > 0,
      onOpenFloatingBanners,
      FloatingBannersModal,
    }),
    [FloatingBannersModal, floatingBannerItems.length, onOpenFloatingBanners]
  );

  return (
    <FloatingBannersContext.Provider value={value}>
      {children}
    </FloatingBannersContext.Provider>
  );
};

export const useFloatingBanners = () => {
  const context = useContext(FloatingBannersContext);
  if (!context) {
    throw new Error(
      "useFloatingBanners must be used inside FloatingBannersProvider"
    );
  }
  return context;
};
